import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Modal from "@/components/Modal";
import TabBar from "@/components/TabBar";
import SortableTh, { sortRows } from "@/components/SortableTh";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Lock, ExternalLink } from "lucide-react";

export default function Companies() {
    const { user } = useAuth();
    const isAdmin = ["admin", "superadmin"].includes(user?.role);
    const isSuper = user?.role === "superadmin";
    const [rows, setRows] = useState([]);
    const [error, setError] = useState("");
    const [q, setQ] = useState("");
    const [modal, setModal] = useState(null); // { mode: 'create'|'edit', company }
    const [tab, setTab] = useState("general");
    const [form, setForm] = useState({ name: "", domain: "", notes: "", is_confidential: false, is_internal_use: false });
    const [members, setMembers] = useState({ loading: false, data: [] });
    const [slaInfo, setSlaInfo] = useState({ loading: false, data: null });
    const [saving, setSaving] = useState(false);
    const [sort, setSort] = useState({ field: "name", dir: "asc" });

    const load = async () => {
        try { const { data } = await api.get("/companies"); setRows(data); }
        catch (e) { setError(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setForm({ name: "", domain: "", notes: "", is_confidential: false, is_internal_use: false });
        setTab("general");
        setModal({ mode: "create" });
    };
    const openEdit = (c) => {
        setForm({ name: c.name, domain: c.domain || "", notes: c.notes || "", is_confidential: !!c.is_confidential, is_internal_use: !!c.is_internal_use });
        setTab("general");
        setModal({ mode: "edit", company: c });
    };

    // Lazy load para tabs Personas y SLAs
    useEffect(() => {
        if (!modal || modal.mode === "create") return;
        if (tab === "members" && members.data.length === 0 && !members.loading) {
            (async () => {
                setMembers({ loading: true, data: [] });
                try {
                    const { data } = await api.get("/users");
                    const cid = modal.company.company_id;
                    const filtered = (data || []).filter((u) =>
                        (u.company_assignments || []).some((a) => a.company_id === cid) || u.company_id === cid
                    );
                    setMembers({ loading: false, data: filtered });
                } catch { setMembers({ loading: false, data: [] }); }
            })();
        }
        if (tab === "sla" && !slaInfo.data && !slaInfo.loading) {
            (async () => {
                setSlaInfo({ loading: true, data: null });
                try {
                    const { data } = await api.get("/sla/policies");
                    setSlaInfo({ loading: false, data });
                } catch { setSlaInfo({ loading: false, data: null }); }
            })();
        }
    }, [tab, modal, members.data.length, members.loading, slaInfo.data, slaInfo.loading]);

    // Reset cached data al cerrar modal o cambiar empresa
    useEffect(() => {
        if (!modal) {
            setMembers({ loading: false, data: [] });
            setSlaInfo({ loading: false, data: null });
        }
    }, [modal]);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true); setError("");
        try {
            if (modal.mode === "create") {
                await api.post("/companies", form);
            } else {
                await api.patch(`/companies/${modal.company.company_id}`, form);
            }
            setModal(null);
            await load();
        } catch (err) { setError(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const remove = async (c) => {
        if (!window.confirm(`¿Eliminar empresa "${c.name}"? Sus usuarios pasarán a "POR ASIGNAR".`)) return;
        try { await api.delete(`/companies/${c.company_id}`); await load(); }
        catch (err) { setError(formatApiError(err)); }
    };

    const filtered = useMemo(() => {
        const base = rows.filter((r) =>
            !q || r.name.toLowerCase().includes(q.toLowerCase()) || (r.domain || "").toLowerCase().includes(q.toLowerCase())
        );
        return sortRows(base, sort);
    }, [rows, q, sort]);

    return (
        <div data-testid="companies-page">
            <div className="page-head">
                <div>
                    <h1>Empresas</h1>
                    <p>Catálogo de empresas. Cada cliente pertenece a una empresa.</p>
                </div>
                {isAdmin && (
                    <button className="btn btn-primary" onClick={openCreate} data-testid="company-create-btn">
                        <Plus size={16} /> Nueva empresa
                    </button>
                )}
            </div>

            {error && <div className="alert alert-error" data-testid="companies-error">{error}</div>}

            <div className="card">
                <div className="card-header">
                    <h3>Listado</h3>
                    <div className="tbar"><input className="input" style={{ width: 280 }} placeholder="Buscar por nombre o dominio…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="companies-search" /></div>
                </div>
                <div className="table-wrap">
                    <table className="t">
                        <thead><tr><SortableTh field="name" sort={sort} onChange={setSort}>Nombre</SortableTh><SortableTh field="domain" sort={sort} onChange={setSort}>Dominio</SortableTh><SortableTh field="notes" sort={sort} onChange={setSort}>Notas</SortableTh><SortableTh field="company_id" sort={sort} onChange={setSort}>ID</SortableTh>{isAdmin && <th style={{ width: 140, textAlign: "right" }}>Acciones</th>}</tr></thead>
                        <tbody>
                            {filtered.length === 0 && (<tr><td colSpan={isAdmin ? 5 : 4} className="text-muted" style={{ textAlign: "center", padding: 30 }}>Sin resultados.</td></tr>)}
                            {filtered.map((c) => (
                                <tr key={c.company_id} data-testid={`company-row-${c.company_id}`}>
                                    <td>
                                        <b>{c.name}</b>
                                        {c.is_placeholder && <span className="badge" style={{ marginLeft: 8 }}>Placeholder</span>}
                                        {c.is_special === "all" && <span className="badge" style={{ marginLeft: 8, background: "#1d4ed8", color: "white" }}>TODAS</span>}
                                        {c.is_confidential && <span className="badge" style={{ marginLeft: 8, background: "#991B1B", color: "white" }} data-testid={`company-confidential-${c.company_id}`}>Confidencial</span>}
                                        {c.is_internal_use && <span className="badge" style={{ marginLeft: 8, background: "#F97316", color: "#fff", borderColor: "#EA580C" }} data-testid={`company-internal-use-${c.company_id}`}>Uso Interno</span>}
                                    </td>
                                    <td className="text-muted">{c.domain || "—"}</td>
                                    <td className="text-muted" style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.notes || "—"}</td>
                                    <td className="id-mono">{c.company_id}</td>
                                    {isAdmin && (
                                        <td>
                                            <div className="actions">
                                                {!c.is_placeholder && c.is_special !== "all" && (
                                                    <>
                                                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(c)} data-testid={`company-edit-${c.company_id}`}><Pencil size={13} /> Editar</button>
                                                        <button className="btn btn-danger btn-sm" onClick={() => remove(c)} data-testid={`company-delete-${c.company_id}`}><Trash2 size={13} /></button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal
                open={!!modal}
                onClose={() => setModal(null)}
                title={modal?.mode === "create" ? "Nueva empresa" : "Editar empresa"}
                testId="company-modal"
                footer={
                    <>
                        <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancelar</button>
                        <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="company-modal-save">
                            {saving ? <span className="loader" /> : "Guardar"}
                        </button>
                    </>
                }
            >
                <form onSubmit={save} className="row" style={{ gap: 14 }}>
                    {modal?.mode === "edit" && (
                        <TabBar
                            active={tab}
                            onChange={setTab}
                            testIdPrefix="company-modal"
                            tabs={[
                                { key: "general", label: "Datos básicos" },
                                { key: "members", label: "Personas asignadas" },
                                { key: "sla", label: "SLAs" },
                            ]}
                        />
                    )}

                    {/* TAB: Datos básicos */}
                    <div style={{ display: modal?.mode === "create" || tab === "general" ? "block" : "none" }} className="row" data-testid="company-modal-tab-general-content">
                        <div className="field">
                            <label>Nombre *</label>
                            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="company-modal-name" />
                        </div>
                        <div className="field">
                            <label>Dominio (ej. empresa.com)</label>
                            <input className="input" value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} data-testid="company-modal-domain" />
                        </div>
                        <div className="field">
                            <label>Notas</label>
                            <textarea className="textarea" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="company-modal-notes" />
                        </div>
                        <div className="field">
                            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: isSuper ? "pointer" : "not-allowed", userSelect: "none", opacity: isSuper ? 1 : 0.6 }}>
                                <input
                                    type="checkbox"
                                    checked={!!form.is_confidential}
                                    disabled={!isSuper}
                                    onChange={(e) => setForm({ ...form, is_confidential: e.target.checked })}
                                    data-testid="company-modal-confidential"
                                />
                                <span>
                                    <b>Confidencial</b>
                                    {!isSuper && (
                                        <span className="badge" style={{ marginLeft: 8, background: "var(--bg-2)", borderColor: "var(--border-strong)", color: "var(--text-muted)", fontSize: 10 }} data-testid="company-modal-confidential-locked">
                                            <Lock size={9} style={{ marginRight: 3, verticalAlign: "middle" }} /> Solo superadmin
                                        </span>
                                    )}
                                    <div className="text-muted" style={{ fontSize: 12 }}>
                                        Los tickets de esta empresa solo serán visibles para personas asignadas como administradoras explícitas. Los administradores con la empresa "TODAS" no verán estos tickets.
                                    </div>
                                </span>
                            </label>
                        </div>
                        <div className="field">
                            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}>
                                <input
                                    type="checkbox"
                                    checked={!!form.is_internal_use}
                                    onChange={(e) => setForm({ ...form, is_internal_use: e.target.checked })}
                                    data-testid="company-modal-internal-use"
                                />
                                <span>
                                    <b>Uso Interno</b>
                                    <div className="text-muted" style={{ fontSize: 12 }}>
                                        Solo personas marcadas como "Uso Interno" pueden asignarse a esta empresa. Aplica a todos los roles, incluidos administradores y superadmin.
                                    </div>
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* TAB: Personas asignadas (solo en edit) */}
                    {modal?.mode === "edit" && (
                        <div style={{ display: tab === "members" ? "block" : "none" }} data-testid="company-modal-tab-members-content">
                            {members.loading && <div className="text-muted" style={{ padding: 12 }}>Cargando personas…</div>}
                            {!members.loading && members.data.length === 0 && (
                                <div className="text-muted" style={{ padding: 18, fontSize: 13 }}>
                                    Esta empresa aún no tiene personas asignadas. Ve a <Link to="/admin/agents" className="linklike">Personas</Link> para asignarlas.
                                </div>
                            )}
                            {!members.loading && members.data.length > 0 && (
                                <>
                                    <p className="text-muted" style={{ fontSize: 12, margin: "0 0 8px" }}>
                                        {members.data.length} persona(s) asignadas. Gestiona altas/bajas desde la pantalla de <Link to="/admin/agents" className="linklike" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>Personas <ExternalLink size={11} /></Link>.
                                    </p>
                                    <div className="table-wrap">
                                        <table className="t" style={{ fontSize: 13 }}>
                                            <thead><tr><th>Nombre</th><th>Correo</th><th>Rol</th><th style={{ textAlign: "center" }}>Admin</th></tr></thead>
                                            <tbody>
                                                {members.data.map((u) => {
                                                    const assignment = (u.company_assignments || []).find((a) => a.company_id === modal.company.company_id);
                                                    const isAdminInCo = !!assignment?.is_admin;
                                                    return (
                                                        <tr key={u.user_id} data-testid={`company-member-${u.user_id}`}>
                                                            <td><b>{u.name}</b></td>
                                                            <td className="text-muted" style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 12 }}>{u.email}</td>
                                                            <td><span className={`badge role-${u.role}`}>{u.role}</span></td>
                                                            <td style={{ textAlign: "center" }}>{isAdminInCo ? "✓" : "—"}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* TAB: SLAs (solo en edit) */}
                    {modal?.mode === "edit" && (
                        <div style={{ display: tab === "sla" ? "block" : "none" }} data-testid="company-modal-tab-sla-content">
                            {slaInfo.loading && <div className="text-muted" style={{ padding: 12 }}>Cargando SLAs…</div>}
                            {!slaInfo.loading && slaInfo.data && (
                                <>
                                    <p className="text-muted" style={{ fontSize: 12, margin: "0 0 12px" }}>
                                        Política SLA global aplicada a todas las empresas (no hay políticas por empresa por ahora). Las SLAs aplican según la prioridad del ticket.
                                    </p>
                                    <div className="table-wrap">
                                        <table className="t" style={{ fontSize: 13 }}>
                                            <thead><tr><th>Prioridad</th><th>1ª respuesta</th><th>Resolución</th><th>Descripción</th></tr></thead>
                                            <tbody>
                                                {Object.entries(slaInfo.data.policies || {}).map(([prio, p]) => (
                                                    <tr key={prio} data-testid={`company-sla-${prio}`}>
                                                        <td><span className="badge">{prio}</span></td>
                                                        <td className="mono" style={{ fontSize: 12 }}>{Math.round((p.first_response_min || 0) / 60 * 10) / 10}h</td>
                                                        <td className="mono" style={{ fontSize: 12 }}>{Math.round((p.resolution_min || 0) / 60 * 10) / 10}h</td>
                                                        <td className="text-muted" style={{ fontSize: 12 }}>{slaInfo.data.descriptions?.[prio] || "—"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <p className="text-muted" style={{ fontSize: 12, margin: "12px 0 0" }}>
                                        💡 Próximamente: políticas SLA por empresa y horario laboral (excluir fines de semana, festivos, fuera de oficina).
                                    </p>
                                </>
                            )}
                        </div>
                    )}
                </form>
            </Modal>
        </div>
    );
}
